import React, { forwardRef, ForwardedRef, ReactNode } from 'react';
import clsx from 'clsx';
import { filterDOMProps } from '@react-aria/utils';
import { AriaLabelingProps } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/list/list.module.css';

import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { AriaLabelingPropsKeys, GlobalDomAttributes } from '../../utils/types';

export interface ListItemTextProps
  extends Pick<AriaLabelingProps, AriaLabelingPropsKeys>,
    SapphireStyleProps,
    GlobalDomAttributes {
  /**
   * The label text, placed on top of the primary text.
   */
  label?: ReactNode;

  /**
   * The most emphasized text.
   */
  primary: ReactNode;

  /**
   * The least emphasized text.
   */
  secondary?: ReactNode;

  /**
   * Reverses order of primary and secondary text,
   * so that secondary text is displayed above the primary.
   *
   * @deprecated Will be removed in v6. use `label` prop instead.
   */
  reverseOrder?: boolean;
}

export const ListItemText = forwardRef(function ListItemText(
  props: ListItemTextProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const { label, primary, secondary, reverseOrder, ...otherProps } = props;
  const { styleProps } = useSapphireStyleProps(otherProps);

  const labelElement = label && (
    <div className={clsx(styles['sapphire-list__item-text-label'])}>
      {label}
    </div>
  );
  const primaryElement = primary && (
    <div className={clsx(styles['sapphire-list__item-text-primary'])}>
      {primary}
    </div>
  );
  const secondaryElement = secondary && (
    <div className={clsx(styles['sapphire-list__item-text-secondary'])}>
      {secondary}
    </div>
  );

  return (
    <div
      className={clsx(styles['sapphire-list__item-text'], styleProps.className)}
      style={styleProps.style}
      {...filterDOMProps(otherProps, { global: true })}
      ref={ref}
    >
      {labelElement}
      {/* TODO remove in v6 deprecated */}
      {reverseOrder ? secondaryElement : primaryElement}
      {reverseOrder ? primaryElement : secondaryElement}
    </div>
  );
});
